<template>
  <div class="mm-fields">
    <div v-if="locationType == 1" class="mm-field">
      <label :for="`${idPrefix}-account`" class="mm-label">
        <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="lucide lucide-map-pin w-3.5 h-3.5 text-muted-foreground" aria-hidden="true" data-replit-metadata="artifacts/analytics-dashboard/src/components/admin/InvitationFields.tsx:101:12" data-component-name="MapPin"><path d="M20 10c0 4.993-5.539 10.193-7.399 11.799a1 1 0 0 1-1.202 0C9.539 20.193 4 14.993 4 10a8 8 0 0 1 16 0"></path><circle cx="12" cy="10" r="3"></circle></svg> Account / Location
      </label>
      <select
        :id="`${idPrefix}-account`"
        class="mm-select"
        :value="localValue.locationId"
        @change="onAccount($event.target.value)"
      >
        <option value="" disabled>Select an account</option>
        <option v-for="loc in locations" :key="loc.id" :value="String(loc.id)">
          {{ loc.name }}
        </option>
      </select>
    </div>

    <div class="mm-field" v-if="pathwaysLoading == false">
      <label :for="`${idPrefix}-pathway`" class="mm-label">
        <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="lucide lucide-route w-3.5 h-3.5 text-muted-foreground" aria-hidden="true" data-replit-metadata="artifacts/analytics-dashboard/src/components/admin/InvitationFields.tsx:127:10" data-component-name="Route"><circle cx="6" cy="19" r="3"></circle><path d="M9 19h8.5a3.5 3.5 0 0 0 0-7h-11a3.5 3.5 0 0 1 0-7H15"></path><circle cx="18" cy="5" r="3"></circle></svg> Pathway
      </label>
      <select
        :id="`${idPrefix}-pathway`"
        class="mm-select"
        :value="localValue.pathwayId"
        :disabled="pathwaysLoading"
        @change="onPathway($event.target.value)"
      >
        <option value="">
          {{ pathwaysLoading ? "Loading pathways…" : "Select a pathway" }}
        </option>
        <option v-for="pw in pathways" :key="pw.id" :value="String(pw.id)">
          {{ pw.name }}
        </option>
      </select>
    </div>

    <div class="mm-field" v-if="stepsLoading == false">
      <label :for="`${idPrefix}-step`" class="mm-label">
        <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="lucide lucide-footprints w-3.5 h-3.5 text-muted-foreground" aria-hidden="true" data-replit-metadata="artifacts/analytics-dashboard/src/components/admin/InvitationFields.tsx:153:10" data-component-name="Footprints"><path d="M4 16v-2.38C4 11.5 2.97 10.5 3 8c.03-2.72 1.49-6 4.5-6C9.37 2 10 3.8 10 5.5c0 3.11-2 5.66-2 8.68V16a2 2 0 1 1-4 0Z"></path><path d="M20 20v-2.38c0-2.12 1.03-3.12 1-5.62-.03-2.72-1.49-6-4.5-6C14.63 6 14 7.8 14 9.5c0 3.11 2 5.66 2 8.68V20a2 2 0 1 0 4 0Z"></path><path d="M16 17h4"></path><path d="M4 13h4"></path></svg> Pathway Step
      </label>
      <select
        :id="`${idPrefix}-step`"
        class="mm-select"
        :value="localValue.stepId"
        :disabled="!localValue.pathwayId || stepsLoading"
        @change="onStep($event.target.value)"
      >
        <option value="">
          {{ stepsLoading ? "Loading steps…" : "Select a step" }}
        </option>
        <option v-for="step in steps" :key="step.id" :value="String(step.id)">
          {{ step.name }}
        </option>
      </select>
    </div>

    <div class="mm-field">
      <label :for="`${idPrefix}-password`" class="mm-label">
        <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="lucide lucide-key-round w-3.5 h-3.5 text-muted-foreground" aria-hidden="true" data-replit-metadata="artifacts/analytics-dashboard/src/components/admin/InvitationFields.tsx:181:10" data-component-name="KeyRound"><path d="M2.586 17.414A2 2 0 0 0 2 18.828V21a1 1 0 0 0 1 1h3a1 1 0 0 0 1-1v-1a1 1 0 0 1 1-1h1a1 1 0 0 0 1-1v-1a1 1 0 0 1 1-1h.172a2 2 0 0 0 1.414-.586l.814-.814a6.5 6.5 0 1 0-4-4z"></path><circle cx="16.5" cy="7.5" r=".5" fill="currentColor"></circle></svg> Access password
      </label>
      <div class="mm-row">
        <input
          :id="`${idPrefix}-password`"
          class="mm-input mm-mono"
          :type="showPw ? 'text' : 'password'"
          :value="localValue.password"
          placeholder="Set a password"
          readonly="readonly"
          @input="emitPatch({ password: $event.target.value })"
        />
        <button
          type="button"
          class="mm-icon-btn"
          :aria-label="showPw ? 'Hide password' : 'Show password'"
          @click="showPw = !showPw"
        >
          <svg v-if="showPw" xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="lucide lucide-eye-off w-4 h-4" aria-hidden="true" data-replit-metadata="artifacts/analytics-dashboard/src/components/admin/InvitationQRDisplay.tsx:177:22" data-component-name="EyeOff"><path d="M10.733 5.076a10.744 10.744 0 0 1 11.205 6.575 1 1 0 0 1 0 .696 10.747 10.747 0 0 1-1.444 2.49"></path><path d="M14.084 14.158a3 3 0 0 1-4.242-4.242"></path><path d="M17.479 17.499a10.75 10.75 0 0 1-15.417-5.151 1 1 0 0 1 0-.696 10.75 10.75 0 0 1 4.446-5.143"></path><path d="m2 2 20 20"></path></svg>
          <svg v-else xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="lucide lucide-eye w-4 h-4" aria-hidden="true" data-replit-metadata="artifacts/analytics-dashboard/src/components/admin/InvitationQRDisplay.tsx:177:55" data-component-name="Eye"><path d="M2.062 12.348a1 1 0 0 1 0-.696 10.75 10.75 0 0 1 19.876 0 1 1 0 0 1 0 .696 10.75 10.75 0 0 1-19.876 0"></path><circle cx="12" cy="12" r="3"></circle></svg>
        </button>
        <button
          type="button"
          class="mm-icon-btn"
          aria-label="Generate a new password"
          @click="emitPatch({ password: genPassword() })"
        >
          <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="lucide lucide-refresh-cw w-4 h-4" aria-hidden="true" data-replit-metadata="artifacts/analytics-dashboard/src/components/admin/InvitationFields.tsx:211:12" data-component-name="RefreshCw"><path d="M3 12a9 9 0 0 1 9-9 9.75 9.75 0 0 1 6.74 2.74L21 8"></path><path d="M21 3v5h-5"></path><path d="M21 12a9 9 0 0 1-9 9 9.75 9.75 0 0 1-6.74-2.74L3 16"></path><path d="M8 16H3v5"></path></svg>
        </button>
      </div>
      <p class="mm-hint">
        The mentee must enter this password to join — share it separately from the link.
      </p>
    </div>
  </div>
</template>

<script>
import axios from 'axios';

export default {
  name: "InvitationFields",
  props: {
    value: { type: Object, required: true, default: () => ({}) },
    locations: { type: Array, default: () => [] },
    idPrefix: { type: String, default: "inv" },
    apiBase: { type: String, default: "" },
    defaultLocationId: { type: String, default: "" },
    locationType: { type: String, default: "" },
  },
  data() {
    return {
      showPw: true,
      pathways: [],
      steps: [],
      pathwaysLoading: false,
      stepsLoading: false,
      localValue: Object.assign({}, this.value)
    };
  },
  computed: {
    singleLocation() {
      return this.locations.length === 1 ? this.locations[0] : null;
    },
    stepPlaceholder() {
      if (!this.value.pathwayId) return "Pick a pathway first";
      if (this.stepsLoading) return "Loading steps…";
      return "Select a step";
    },
  },
  watch: {
    singleLocation: {
      immediate: true,
      handler(loc) {
        if (loc && this.value.locationId !== String(loc.id)) {
          this.emitPatch({
            locationId: String(loc.id),
            locationName: loc.name,
          });
        }
      },
    },
    "value.locationId": {
      immediate: true,
      handler(id) {
        if (id) this.fetchPathways(id);
        else this.pathways = []; this.steps = [];
      },
    },
    "value.pathwayId": {
      immediate: true,
      handler(id) {
        if (id) this.fetchSteps(id);
        else this.steps = [];
      },
    },
  },
  created() {
    if (this.defaultLocationId) {
      this.fetchPathways(this.defaultLocationId);
    }
    
  },
  methods: {
    genPassword() {
      return Math.random().toString(36).slice(2, 8).toUpperCase();
    },
    emitPatch(patch) {
      const currentValue = this.localValue || {};
      const updatedValue = Object.assign({}, currentValue, patch);

      // Ensure Vue tracks every top-level key in the patch
      Object.keys(patch).forEach(key => {
        this.$set(updatedValue, key, patch[key]);
      });
      console.log(updatedValue);
      this.localValue = updatedValue;
      this.$emit("input", updatedValue);
    },
    onAccount(v) {
      const loc = this.locations.find((l) => String(l.id) === v);
      this.emitPatch({ 
          locationId: v,
          locationName: loc ? loc.name : "",
          pathwayId: "",
          pathwayName: "",
          stepId: "",
          stepName: "",
        });
    },
    onPathway(v) {
      const pw = this.pathways.find((p) => String(p.id) === v);
      this.emitPatch({
        pathwayId: v,
        pathwayName: pw ? pw.name : "",
        stepId: "",
        stepName: "",
      });
      this.fetchSteps(pw.id);
    },
    onStep(v) {
      const st = this.steps.find((s) => String(s.id) === v);
      this.emitPatch({ stepId: v, stepName: st ? st.name : "" });
    },
    async fetchPathways(id) {
      this.pathwaysLoading = true;
      if (id) {
        axios('/get-location-pathway-create-invite',
        {
          method: 'GET',
          params: {
            accountId: id,
          }
        })
        .then(resp => {
          this.pathwaysLoading = false;   
          this.pathways = resp.data;      
        })
        .catch(() => {
        });
      }
      
    },
    async fetchSteps(pathwayId) {
      this.stepsLoading = true;
      if (pathwayId) {
        axios('/get-location-pathway-steps-create-invite',
        {
          method: 'GET',
          params: {
            pathwayId: pathwayId,
          }
        })
        .then(resp => {
          this.stepsLoading = false;   
          this.steps = resp.data;       
        })
        .catch(() => {
        });
      }
      
      
    },
  },
};
</script>

<style scoped>
.mm-fields {
  display: flex;
  flex-direction: column;
  gap: 20px;
  margin-block-end: calc(calc(0.25rem * 5) * calc(1 - 0));
}
.mm-field {
  display: flex;
  flex-direction: column;
  gap: 8px;
}
.mm-label {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 14px;
  font-weight: 700;
  color: #1f2a30;
  margin-bottom: 0 !important;
}
.mm-select,
.mm-input {
  width: 100%;
  box-sizing: border-box;
  height: 40px;
  padding: 0 12px;
  border: 1px solid #d6dde1;
  border-radius: 8px;
  font-size: 14px;
  color: #1f2a30;
  background: #fff;
  outline: none;
}
.mm-select:focus,
.mm-input:focus {
  border-color: #00acee;
  box-shadow: 0 0 0 3px rgba(0, 172, 238, 0.15);
}
.mm-select:disabled,
.mm-input:disabled {
  background: #f3f5f6;
  color: #9aa6ac;
  cursor: not-allowed;
}
.mm-row {
  display: flex;
  gap: 8px;
}
.mm-mono {
  font-family: ui-monospace, SFMono-Regular, Menlo, Consolas, monospace;
}
.mm-icon-btn {
  flex: 0 0 auto;
  width: 40px;
  height: 40px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border: 1px solid #d6dde1;
  border-radius: 8px;
  background: #fff;
  color: #1f2a30;
  cursor: pointer;
}
.mm-icon-btn:hover {
  background: #f3f5f6;
}
.mm-hint {
  margin: 0;
  font-size: 11px;
  color: #6b7785;
}
.text-muted-foreground {
    color: #627384;
}
</style>



// WEBPACK FOOTER //
// src/components/manager/InvitationFields.vue